import React, { useState, useEffect } from "react";
import Modal from "components/modal";

const AnneUniversitaireFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialAnneUniv, 
  isEditing,
  loading 
}) => {
  const [anneUniv, setAnneUniv] = useState({
    dateuniv: "",
    statusuniv: "En cours"
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Options de statut
  const statusOptions = ["En cours", "Terminé", "À venir"];
  
  useEffect(() => {
    if (isOpen && initialAnneUniv) {
      setAnneUniv({
        dateuniv: initialAnneUniv.dateuniv || "",
        statusuniv: initialAnneUniv.statusuniv || "En cours",
      });
      setValidationErrors({});
    }
  }, [isOpen, initialAnneUniv]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAnneUniv({
      ...anneUniv,
      [name]: value,
    });

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!anneUniv.dateuniv.trim()) {
      errors.dateuniv = "L'année universitaire est requise";
    } else if (!isValidDateuniv(anneUniv.dateuniv)) {
      errors.dateuniv = "Le format doit être AAAA-AAAA (ex: 2025-2026)";
    }

    if (!anneUniv.statusuniv.trim()) {
      errors.statusuniv = "Le statut est requis";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidDateuniv = (value) => {
    // Vérifie si le format est AAAA-AAAA et si la seconde année est la première + 1
    const regex = /^(\d{4})-(\d{4})$/;
    const match = value.match(regex);
    if (!match) return false;
    
    const firstYear = parseInt(match[1], 10);
    const secondYear = parseInt(match[2], 10);
    
    return secondYear === firstYear + 1;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit({
      dateuniv: anneUniv.dateuniv,
      statusuniv: anneUniv.statusuniv,
    });
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="md">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier l'année universitaire"
              : "Créer une nouvelle année universitaire"}
          </h3>
          <div className="h-1 w-12 bg-[#57A863] mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="dateuniv"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Année universitaire
            </label>
            <input
              type="text"
              name="dateuniv"
              id="dateuniv"
              placeholder="Ex: 2025-2026"
              value={anneUniv.dateuniv}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
            />
            {validationErrors.dateuniv && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.dateuniv}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="statusuniv"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Statut
            </label>
            <select
              name="statusuniv"
              id="statusuniv"
              value={anneUniv.statusuniv}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {validationErrors.statusuniv && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.statusuniv}
              </p>
            )}
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-[#57A863] text-white hover:bg-[#4c9657] transition-colors shadow-sm"
              disabled={submitting || loading}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Créer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AnneUniversitaireFormModal;